import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Clock, FileText, ShieldCheck, ArrowLeft } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { IntegrityScoreInfo } from "./IntegrityScoreInfo";
import { MonitoringDisclosure } from "./MonitoringDisclosure";
import { useNavigate, Link } from "react-router-dom";
import { COMPANY_CONFIGS } from "@/config/companies";

interface TestStartScreenProps {
  testTitle: string;
  questionCount: number;
  employerOrganization?: string;
  employerMessage?: string;
  testId?: string;
  organizationId?: string;
  isPreview?: boolean;
  onStart: () => void;
}

export function TestStartScreen({
  testTitle,
  questionCount,
  employerOrganization,
  employerMessage,
  testId,
  organizationId,
  isPreview,
  onStart,
}: TestStartScreenProps) {
  const navigate = useNavigate();

  const handleCancel = () => {
    // If this is an employer/collaborator preview, go back to test details
    if (isPreview && testId) {
      navigate(`/test-details/${testId}`);
      return;
    }
    
    // Check if this is an Innoventure test
    if (organizationId === COMPANY_CONFIGS.innoventure.organizationId && testId) {
      navigate(`/innoventure-test/${testId}`);
      return;
    }
    
    // For regular tests, go to candidate dashboard
    if (testId) {
      navigate('/dashboard');
      return;
    }
    
    // Fallback: try browser history
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-3xl w-full border-border">
        <CardContent className="pt-8 pb-8 px-6 md:px-12">
          <div className="space-y-6">
            {/* Header */}
            <div className="text-center space-y-3">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                Welcome to the {testTitle} Assessment
              </h1>
              <p className="text-muted-foreground">
                This test consists of <span className="font-semibold text-foreground">{questionCount}</span> questions
              </p>
            </div>

            <Separator />

            {/* Important Guidelines - Merged from TestWarningDialog */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                Important Test Guidelines
              </h2>
              <p className="text-sm text-muted-foreground">
                To maintain test integrity and ensure fair assessment, please note the following:
              </p>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-3">
                  <ShieldCheck className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>
                    <strong className="text-foreground">No external tools permitted:</strong> The use of AI tools, 
                    content generators, or unauthorized resources is strictly prohibited during this assessment.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>
                    <strong className="text-foreground">Stay focused:</strong> Avoid switching between browser 
                    windows or applications. Complete the test in one sitting.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong className="text-foreground">Active monitoring:</strong> This test uses integrity monitoring 
                    methods including webcam snapshots and activity tracking to ensure fair assessment.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong className="text-foreground">Disconnection handling:</strong> If you lose connection or 
                    accidentally close the browser, you can refresh the page and an option to resume will appear.
                  </span>
                </li>
              </ul>
            </div>

            <Separator />

            {/* Monitoring Disclosure - from TestWarningDialog */}
            <MonitoringDisclosure />

            {/* Integrity Score Information */}
            <IntegrityScoreInfo />

            {/* Employer Custom Message */}
            {employerMessage && (
              <>
                <Separator />
                <div className="bg-muted/50 rounded-lg p-6 space-y-3">
                  <h2 className="text-lg font-semibold text-foreground">
                    Message from the Test Creators
                  </h2>
                  <div 
                    className="text-sm text-muted-foreground prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: employerMessage }}
                  />
                </div>
              </>
            )}

            {/* Start Button */}
            <div className="pt-4">
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6">
                <p className="text-sm text-center text-foreground">
                  <strong>Important:</strong> By clicking the button below, you acknowledge that you understand 
                  and accept these guidelines. You also agree to{" "}
                  <Link to="/privacy-policy" target="_blank" className="text-primary hover:underline font-medium">
                    Testera's Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link to="/terms-and-conditions" target="_blank" className="text-primary hover:underline font-medium">
                    Terms of Service
                  </Link>
                  , and consent to your data (including webcam snapshots, audio recordings, and activity) 
                  being recorded and processed for assessment integrity purposes. The test will begin immediately.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button 
                  variant="outline" 
                  onClick={handleCancel} 
                  className="flex items-center gap-2 w-full"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Cancel and Go Back
                </Button>
                <Button 
                  onClick={onStart} 
                  size="lg" 
                  className="w-full font-semibold text-lg py-6"
                >
                  I Understand and Accept - Start Test
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
